// Time and size text of the Requests UI. Contract 3.4: the browser zone is never an implicit input or output. An instant is shown in
// UTC with the word "UTC". A wall time (a date and a time the customer wants) is shown as it was entered, with its IANA zone.

const INSTANT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** "17 Oct 2026, 14:03 UTC". An unreadable value is shown as a dash, never as "Invalid Date". */
export function formatInstant(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${INSTANT.format(date)} UTC`;
}

/** "19:30 Europe/London" or "Europe/London". The date part is shown by the caller. */
export function formatWallTime(
  time: string | null | undefined,
  timeZone: string | null | undefined,
): string | null {
  const parts = [time ?? null, timeZone ?? null].filter(
    (part): part is string => part !== null && part !== "",
  );
  return parts.length === 0 ? null : parts.join(" ");
}

/** "2 d 3 h", "5 h 10 min", "12 min", "under a minute". Data, not an SLA: it never judges. */
export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "Not submitted";
  if (seconds < 60) return "under a minute";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${days} d` : `${days} d ${restHours} h`;
}

/** "1.5 MB", "300 KB", "12 bytes". Binary units, one decimal at most. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Number(kb.toFixed(1))} KB`;
  return `${Number((kb / 1024).toFixed(1))} MB`;
}

/** "5 min ago", "3 h ago", "2 d ago", "just now". `now` is the clock of the runtime, so a test and the page agree. */
export function formatAgo(value: string | null | undefined, now: Date): string {
  if (value === null || value === undefined) return "—";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "—";
  const seconds = Math.max(0, Math.floor((now.getTime() - then) / 1000));
  if (seconds < 60) return "just now";
  return `${formatAge(seconds)} ago`;
}
